/**
 * Podešavanja restorana: ekrani, registar polja i pretvaranje između podataka sa servera (`restaurant.settings`)
 * i modela forme. Bez Vue-a, pa se isto koristi na računaru, telefonu i u provjeri.
 *
 * Model forme ima isti oblik kao u tabli: vrijeme je jedan tekst "HH:MM" (na serveru su dva polja), dani su
 * brojevi ili tekst kakav je unesen. `toApi` mijenja samo ono što je korisnik izmijenio, pa ostali podaci
 * (i polja koja ova stranica ne zna) ostaju tačno onakvi kakve je server poslao.
 */
import convert from "cyrillic-to-latin";
import { createRestaurantSettings } from "@/models/restaurantSettings";
import {
  DAYS,
  MAX_CANCEL_MINUTES,
  MAX_ORDER_AHEAD,
  MAX_TIMER,
  isBlank,
  okDays,
  okInt,
  okMoney,
  okTime,
  okUrl,
  toMin,
} from "./settingsLogic";

export const DEFAULT_SCREEN = "deadlines";

/** Grupe gornje navigacije (naziv grupe iznad kartica); redosled kartica = prečice 1–7. */
export const GROUPS = [
  { key: "corporate", titleKey: "settings_shell.groups.corporate" },
  { key: "guests", titleKey: "settings_shell.groups.guests" },
  { key: "basic", titleKey: "settings_shell.groups.basic" },
];

export const SCREENS = [
  { key: "deadlines", group: "corporate", icon: "mdi-clock-outline" },
  { key: "options", group: "corporate", icon: "mdi-tune-variant" },
  { key: "offer", group: "guests", icon: "mdi-storefront-outline" },
  { key: "delivery", group: "guests", icon: "mdi-truck-delivery-outline" },
  { key: "appearance", group: "basic", icon: "mdi-image-multiple-outline" },
  { key: "language", group: "basic", icon: "mdi-translate" },
  { key: "links", group: "basic", icon: "mdi-link-variant" },
];

export const screenByKey = (key) => SCREENS.find((screen) => screen.key === key);

const S = "settings_shell";
/**
 * Stavke koje pretraga nalazi: ekran, sidro (id elementa bez "ss-"), ključ naslova i opisa (isti prevodi kao na ekranu,
 * pa pretraga radi na jeziku korisnika) ili `literal` za nazive koji se ne prevode (Facebook, Google Play…).
 */
export const SEARCH_INDEX = [
  { screen: "deadlines", id: "rok", title: `${S}.deadlines.title`, desc: `${S}.deadlines.desc` },
  { screen: "deadlines", id: "rok", title: `${S}.quick.cycle`, desc: `${S}.quick.cycle_d` },
  { screen: "deadlines", id: "sim", title: `${S}.sim.title`, desc: `${S}.sim.desc` },
  { screen: "deadlines", id: "sw-no_time_limit", title: `${S}.deadlines.no_limit`, desc: `${S}.deadlines.no_limit_d` },
  { screen: "options", id: "sw-izvestaj", title: `${S}.options.report`, desc: "delivery_report_available_for_users" },
  { screen: "options", id: "sw-worker", title: `${S}.options.pickup_branch`, desc: "enable_meal_pickup_another_branch" },
  { screen: "options", id: "sw-can_reserve", title: `${S}.options.spare_meals`, desc: "enable_spare_meal_belated_user" },
  { screen: "options", id: "sw-confirm_delivery", title: `${S}.options.auto_delivery`, desc: "automatic_delivery_no_confirm" },
  { screen: "options", id: "f-timer", title: `${S}.options.timer`, desc: `${S}.options.timer_d` },
  { screen: "offer", id: "master", title: "ordering_persons_without_contract", desc: `${S}.offer.master_d` },
  { screen: "offer", id: "sw-guest-meal_in_restaurant", title: `${S}.fields.meal_in_restaurant`, desc: `${S}.offer.meal_in_restaurant_d` },
  { screen: "offer", id: "sw-guest-personal_takeover", title: `${S}.fields.personal_takeover`, desc: `${S}.offer.personal_takeover_d` },
  { screen: "offer", id: "sw-guest-restoran_delivery", title: `${S}.fields.restaurant_delivery`, desc: `${S}.offer.delivery_d` },
  { screen: "offer", id: "sw-guest-post_delivery", title: `${S}.fields.post_delivery`, desc: `${S}.offer.post_d` },
  { screen: "offer", id: "f-guest-post_description", title: "post_description" },
  { screen: "offer", id: "sw-receiving_orders", title: "receiving_orders", desc: `${S}.offer.receiving_d` },
  { screen: "offer", id: "sw-can_order_off", title: `${S}.offer.order_off`, desc: "restaurant_order_off" },
  { screen: "offer", id: "komentari", title: "comments", desc: "comments_guest" },
  { screen: "delivery", id: "vrijeme", title: "delivery_time_set", desc: `${S}.delivery.time_d` },
  { screen: "delivery", id: "sw-check_delivery_time", title: `${S}.delivery.customer_sets`, desc: "customer_can_set_delivery_time" },
  { screen: "delivery", id: "limiti", title: `${S}.delivery.limits`, desc: `${S}.delivery.ahead_d` },
  { screen: "delivery", id: "gdani", title: `${S}.delivery.days_title`, desc: `${S}.delivery.days_d` },
  { screen: "appearance", id: "web", title: `${S}.appearance.web_title`, desc: `${S}.appearance.web_d` },
  { screen: "appearance", id: "mob", title: `${S}.appearance.mob_title`, desc: `${S}.appearance.mob_d` },
  { screen: "language", id: "jezici", title: `${S}.language.apps`, desc: `${S}.language.apps_d` },
  { screen: "language", id: "pismo", title: `${S}.language.alphabet`, desc: `${S}.language.alphabet_d` },
  { screen: "language", id: "valuta", title: `${S}.language.currency`, desc: `${S}.language.currency_d` },
  { screen: "links", id: "mreze", title: `${S}.links.social` },
  { screen: "links", id: "f-facebook", literal: "Facebook" },
  { screen: "links", id: "f-instagram", literal: "Instagram" },
  { screen: "links", id: "f-twitter", literal: "Twitter" },
  { screen: "links", id: "f-linkedin", literal: "Linkedin" },
  { screen: "links", id: "app", title: `${S}.links.apps` },
  { screen: "links", id: "f-google", literal: "Google Play" },
  { screen: "links", id: "f-apple", literal: "Apple Store" },
  { screen: "links", id: "f-huawei", literal: "Huawei Store" },
  { screen: "links", id: "f-webapp", title: "web_application" },
];

export const IMAGE_FIELDS = ["logo", "header", "restoran_image", "logo_mob_header", "logo_mob_footer", "logo_mob_restoran"];
export const LINK_FIELDS = ["facebook", "instagram", "twitter", "linkedin", "google", "apple", "huawei", "webapp"];
export const MOB_LINK_FIELDS = ["mob.baner_link_header", "mob.baner_link_footer", "mob.baner_link_restoran"];
export const GUEST_PICKUP_FIELDS = [
  "guest.meal_in_restaurant",
  "guest.personal_takeover",
  "guest.restoran_delivery",
  "guest.post_delivery",
];
export const OPTION_SWITCHES = ["izvestaj", "worker", "can_reserve", "confirm_delivery"];

export const CURRENCIES = ["KM", "EUR", "DIN"];

/** Jezici aplikacije (isti kodovi kao u LanguageSettingsSection i main.js). */
export const LANGUAGES = [
  ["sr", "Српски - ћирилица"],
  ["sr-lat", "Srpski - Latinica"],
  ["rs", "Ћирилица"],
  ["rs-lat", "Latinica"],
  ["hr", "Hrvatski"],
  ["bs", "Bosanski"],
  ["en", "English"],
  ["mk", "Makedonski"],
  ["si", "Slovenščina"],
  ["ru", "Русский (Ruski)"],
  ["es", "Español (Španski)"],
  ["it", "Italiano (Italijanski)"],
  ["fr", "Français (Francuski)"],
  ["de", "Deutsch (Nemački)"],
  ["zh", "中文 (Kineski)"],
  ["tr", "Türkçe (Turski)"],
  ["uz", "Oʻzbekcha (Uzbečki)"],
];

/** [putanja u modelu forme ("<d>" = dan), ekran, ključ naziva u settings_shell.fields] */
const FIELD_LIST = [
  ["new_time_changes", "deadlines", "new_time_changes"],
  ["days.<d>.day_before_limit", "deadlines", "days_before"],
  ["days.<d>.time", "deadlines", "days_time"],
  ["days.<d>.day_after_limit", "deadlines", "days_after"],
  ["no_time_limit", "deadlines", "no_time_limit"],
  ["izvestaj", "options", "report"],
  ["worker", "options", "pickup_branch"],
  ["can_reserve", "options", "spare_meals"],
  ["confirm_delivery", "options", "auto_delivery"],
  ["timer", "options", "canteen_timer"],
  ["lice_bez_ugovora", "offer", "guest_ordering"],
  ["guest.meal_in_restaurant", "offer", "meal_in_restaurant"],
  ["guest.personal_takeover", "offer", "personal_takeover"],
  ["guest.restoran_delivery", "offer", "restaurant_delivery"],
  ["guest.delivery_price", "offer", "delivery_price"],
  ["guest.minimal_price", "offer", "minimal_price"],
  ["guest.post_delivery", "offer", "post_delivery"],
  ["guest.post_price", "offer", "post_price"],
  ["guest.post_minimal_price", "offer", "post_minimal_price"],
  ["guest.post_description", "offer", "post_description"],
  ["comments_guest", "offer", "comments_guest"],
  ["comments_restaurant", "offer", "comments_restaurant"],
  ["receiving_orders", "offer", "receiving_orders"],
  ["can_order_off", "offer", "can_order_off"],
  ["delivery_from", "delivery", "delivery_from"],
  ["delivery_to", "delivery", "delivery_to"],
  ["check_delivery_time", "delivery", "check_delivery_time"],
  ["guest.dani_bez_menija", "delivery", "days_ahead"],
  ["guest.guest_cancel_order_time", "delivery", "cancel_time"],
  ["guest_day.new_time_changes", "delivery", "guest_days"],
  ["guest_day.<d>.from", "delivery", "guest_from"],
  ["guest_day.<d>.to", "delivery", "guest_to"],
  ["guest_day.<d>.day_before_limit", "delivery", "guest_first_day"],
  ["guest_day.<d>.time", "delivery", "guest_limit"],
  ["guest_day.<d>.day_after_limit", "delivery", "guest_after"],
  ["logo", "appearance", "logo"],
  ["header", "appearance", "header"],
  ["restoran_image", "appearance", "restaurant_image"],
  ["logo_mob_header", "appearance", "mob_header"],
  ["logo_mob_footer", "appearance", "mob_footer"],
  ["logo_mob_restoran", "appearance", "mob_list"],
  ["mob.baner_link_header", "appearance", "link_header"],
  ["mob.baner_link_footer", "appearance", "link_footer"],
  ["mob.baner_link_restoran", "appearance", "link_list"],
  ["language", "language", "languages"],
  ["price", "language", "currency"],
  ["facebook", "links", "facebook"],
  ["instagram", "links", "instagram"],
  ["twitter", "links", "twitter"],
  ["linkedin", "links", "linkedin"],
  ["google", "links", "google"],
  ["apple", "links", "apple"],
  ["huawei", "links", "huawei"],
  ["webapp", "links", "webapp"],
];

export const FIELDS = FIELD_LIST.map(([pattern, screen, label]) => ({ pattern, screen, label }));

const DAY_SEGMENT = new RegExp(`\\.(${DAYS.join("|")})\\.`);
const toPattern = (path) => String(path).replace(DAY_SEGMENT, ".<d>.");
const dayOf = (path) => {
  const match = String(path).match(DAY_SEGMENT);
  return match ? match[1] : null;
};

export const fieldOf = (path) => FIELDS.find((field) => field.pattern === toPattern(path)) || null;
export const screenOfPath = (path) => (fieldOf(path) || {}).screen || null;
/** { key, day } za prevod naziva polja u pregledu izmjena. */
export const labelOfPath = (path) => {
  const field = fieldOf(path);
  return field ? { key: `settings_shell.fields.${field.label}`, day: dayOf(path) } : { key: "", day: null };
};

// ---------------------------------------------------------------- pristup putanji
export const clone = (value) => JSON.parse(JSON.stringify(value === undefined ? null : value));

export const getPath = (obj, path) =>
  String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

/** Upisuje vrijednost; `setter(obj, key, value)` je Vue.set u komponenti (da promjena bude reaktivna). */
export const setPath = (obj, path, value, setter) => {
  const keys = String(path).split(".");
  const last = keys.pop();
  const target = keys.reduce((acc, key) => acc[key], obj);
  if (setter) {
    setter(target, last, value);
  } else {
    target[last] = value;
  }
};

/** Razlaže objekat na listove: { "days.monday.time": "12:00", ... }; nizovi ostaju listovi. */
export const flat = (obj, prefix = "", out = {}) => {
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flat(value, path, out);
    } else {
      out[path] = value;
    }
  });
  return out;
};

/** Isto za "" / null / undefined; broj i tekst su isti ako se isto ispisuju; nizovi se porede po redoslijedu. */
export const same = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a || []) === JSON.stringify(b || []);
  }
  return String(a == null ? "" : a) === String(b == null ? "" : b);
};

/** Izmjene između snimljenog i trenutnog modela: [{ path, from, to }]. */
export const diffForms = (saved, current) => {
  const a = flat(saved);
  const b = flat(current);
  const out = [];
  Object.keys(b).forEach((path) => {
    if (!same(a[path], b[path])) {
      out.push({ path, from: a[path], to: b[path] });
    }
  });
  return out;
};

// ---------------------------------------------------------------- server <-> forma
const isInt = (v) => /^\d{1,2}$/.test(String(v == null ? "" : v).trim());
const pad2 = (v) => String(v).trim().padStart(2, "0");
const validHour = (v) => isInt(v) && Number(v) <= 23;
const validMinute = (v) => isInt(v) && Number(v) <= 59;
const hm = (hour, minute) => (validHour(hour) && validMinute(minute) ? `${pad2(hour)}:${pad2(minute)}` : "");
const splitHm = (value) => {
  const [h, m] = String(value || "").split(":");
  return validHour(h) && validMinute(m) ? [pad2(h), pad2(m)] : ["", ""];
};
const text = (v) => (v == null ? "" : v);
const numOrRaw = (v) => (/^\d+$/.test(String(v == null ? "" : v).trim()) ? Number(String(v).trim()) : v);
const money = (v) => String(text(v)).trim().replace(",", ".");

/** Zamjenjuje nedostajuće ključeve zadanim vrijednostima (duboko); postojeći ostaju kakvi jesu. */
export const withDefaults = (value, defaults) => {
  if (Array.isArray(defaults)) {
    return Array.isArray(value) ? value : defaults.slice();
  }
  if (defaults && typeof defaults === "object") {
    const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    const out = { ...source };
    Object.keys(defaults).forEach((key) => {
      out[key] = withDefaults(source[key], defaults[key]);
    });
    return out;
  }
  return value === undefined ? defaults : value;
};

/** Podešavanja sa servera dopunjena zadanim vrijednostima (to se i šalje nazad). */
export const normalizeApi = (settings, restaurant) => {
  const merged = withDefaults(settings && typeof settings === "object" ? settings : {}, createRestaurantSettings());
  if (restaurant) {
    merged.ekavica = restaurant.lang === "ekavica";
    merged.ijekavica = restaurant.lang === "ijekavica";
  }
  return merged;
};

export const fromApi = (api) => {
  const days = {};
  const guestDay = { new_time_changes: Boolean(api.guest_day.new_time_changes) };
  DAYS.forEach((day) => {
    const d = api.days[day];
    days[day] = {
      day_before_limit: text(d.day_before_limit),
      time: hm(d.time && d.time.time_hour, d.time && d.time.time_minut),
      day_after_limit: text(d.day_after_limit),
    };
    const g = api.guest_day[day];
    const w = g.working_hours || {};
    guestDay[day] = {
      from: hm(w.delivery_from, w.delivery_to),
      to: hm(w.delivery_from1, w.delivery_to1),
      time: hm(g.time && g.time.time_hour, g.time && g.time.time_minut),
      day_before_limit: text(g.day_before_limit),
      day_after_limit: text(g.day_after_limit),
    };
  });
  const guest = api.guest || {};
  return {
    new_time_changes: Boolean(api.new_time_changes),
    days,
    no_time_limit: Boolean(api.no_time_limit),
    izvestaj: Boolean(api.izvestaj),
    worker: Boolean(api.worker),
    can_reserve: Boolean(api.can_reserve),
    confirm_delivery: Boolean(api.confirm_delivery),
    timer: text(api.timer),
    lice_bez_ugovora: Boolean(api.lice_bez_ugovora),
    guest: {
      meal_in_restaurant: Boolean(guest.meal_in_restaurant),
      personal_takeover: Boolean(guest.personal_takeover),
      restoran_delivery: Boolean(guest.restoran_delivery),
      post_delivery: Boolean(guest.post_delivery),
      delivery_price: text(guest.delivery_price),
      minimal_price: text(guest.minimal_price),
      post_price: text(guest.post_price),
      post_minimal_price: text(guest.post_minimal_price),
      post_description: text(guest.post_description),
      dani_bez_menija: text(guest.dani_bez_menija),
      guest_cancel_order_time: text(guest.guest_cancel_order_time),
    },
    comments_guest: Boolean(api.comments_guest),
    comments_restaurant: Boolean(api.comments_restaurant),
    receiving_orders: Boolean(api.receiving_orders),
    can_order_off: Boolean(api.can_order_off),
    delivery_from: hm(api.delivery_from_hour, api.delivery_from_min),
    delivery_to: hm(api.delivery_to_hour, api.delivery_to_min),
    check_delivery_time: Boolean(Number(api.check_delivery_time)),
    guest_day: guestDay,
    logo: api.logo || null,
    header: api.header || null,
    restoran_image: api.restoran_image || null,
    logo_mob_header: api.logo_mob_header || null,
    logo_mob_footer: api.logo_mob_footer || null,
    logo_mob_restoran: api.logo_mob_restoran || null,
    mob: {
      baner_link_header: text(api.mob.baner_link_header),
      baner_link_footer: text(api.mob.baner_link_footer),
      baner_link_restoran: text(api.mob.baner_link_restoran),
    },
    language: Array.isArray(api.language) ? api.language.slice() : [],
    price: api.price || null,
    facebook: text(api.facebook),
    instagram: text(api.instagram),
    twitter: text(api.twitter),
    linkedin: text(api.linkedin),
    google: text(api.google),
    apple: text(api.apple),
    huawei: text(api.huawei),
    webapp: text(api.webapp),
  };
};

const WRITERS = {
  new_time_changes: (o, v) => {
    o.new_time_changes = v;
    if (v) {
      o.old_time_changes = false;
    }
  },
  "days.<d>.day_before_limit": (o, v, d) => (o.days[d].day_before_limit = numOrRaw(v)),
  "days.<d>.day_after_limit": (o, v, d) => (o.days[d].day_after_limit = numOrRaw(v)),
  "days.<d>.time": (o, v, d) => {
    [o.days[d].time.time_hour, o.days[d].time.time_minut] = splitHm(v);
  },
  timer: (o, v) => (o.timer = numOrRaw(v)),
  "guest.delivery_price": (o, v) => (o.guest.delivery_price = money(v)),
  "guest.minimal_price": (o, v) => (o.guest.minimal_price = money(v)),
  "guest.post_price": (o, v) => (o.guest.post_price = money(v)),
  "guest.post_minimal_price": (o, v) => (o.guest.post_minimal_price = money(v)),
  "guest.dani_bez_menija": (o, v) => (o.guest.dani_bez_menija = numOrRaw(v)),
  "guest.guest_cancel_order_time": (o, v) => (o.guest.guest_cancel_order_time = numOrRaw(v)),
  delivery_from: (o, v) => {
    [o.delivery_from_hour, o.delivery_from_min] = splitHm(v);
  },
  delivery_to: (o, v) => {
    [o.delivery_to_hour, o.delivery_to_min] = splitHm(v);
  },
  check_delivery_time: (o, v) => (o.check_delivery_time = v ? 1 : 0),
  // API: working_hours.delivery_from (sat) + delivery_to (minuta) = početak; delivery_from1 + delivery_to1 = kraj
  "guest_day.<d>.from": (o, v, d) => {
    [o.guest_day[d].working_hours.delivery_from, o.guest_day[d].working_hours.delivery_to] = splitHm(v);
  },
  "guest_day.<d>.to": (o, v, d) => {
    [o.guest_day[d].working_hours.delivery_from1, o.guest_day[d].working_hours.delivery_to1] = splitHm(v);
  },
  "guest_day.<d>.time": (o, v, d) => {
    [o.guest_day[d].time.time_hour, o.guest_day[d].time.time_minut] = splitHm(v);
  },
  "guest_day.<d>.day_before_limit": (o, v, d) => (o.guest_day[d].day_before_limit = numOrRaw(v)),
  "guest_day.<d>.day_after_limit": (o, v, d) => (o.guest_day[d].day_after_limit = numOrRaw(v)),
  language: (o, v) => (o.language = v.slice()),
  price: (o, v) => (o.price = v || null),
};
IMAGE_FIELDS.forEach((key) => {
  WRITERS[key] = (o, v) => (o[key] = v || null);
});

/**
 * Šalje se: osnova (podaci sa servera) + samo polja koja su izmijenjena u odnosu na `saved`.
 * @param {object} form trenutni model forme
 * @param {object} saved model forme u trenutku zadnjeg snimanja
 * @param {object} base podaci sa servera u trenutku zadnjeg snimanja (normalizeApi)
 */
export const toApi = (form, saved, base) => {
  const out = clone(base);
  const a = flat(saved);
  const b = flat(form);
  Object.keys(b).forEach((path) => {
    if (same(a[path], b[path])) {
      return;
    }
    const writer = WRITERS[toPattern(path)];
    if (writer) {
      writer(out, b[path], dayOf(path));
    } else {
      setPath(out, path, b[path]);
    }
  });
  return out;
};

// ---------------------------------------------------------------- provjere
const MESSAGES = {
  days: { max: 60 },
  time: {},
  timer: { max: MAX_TIMER },
  money: {},
  cancel: { max: MAX_CANCEL_MINUTES },
  ahead: { max: MAX_ORDER_AHEAD },
  url: {},
  order: {},
  language: {},
};

const err = (code) => ({ code, params: MESSAGES[code] || {} });

/**
 * Provjere unosa. `isDirty(path)` kaže da li je korisnik izmijenio polje: greška se računa samo za izmijenjena
 * polja, da stare vrijednosti (npr. cijena upisana kao "3 KM") ne zaključaju snimanje nečeg drugog.
 * Upozorenja (warnings) ne zabranjuju snimanje.
 * @returns {{ errors: Object<string,{code:string,params:object}>, warnings: Object<string,{code:string,params:object}> }}
 */
export const validateForm = (c, isDirty) => {
  const errors = {};
  const warnings = {};
  const check = (path, fn) => {
    if (isDirty(path)) {
      const code = fn();
      if (code) {
        errors[path] = err(code);
      }
    }
  };
  const days = (path) => check(path, () => (!isBlank(getPath(c, path)) && !okDays(getPath(c, path)) ? "days" : ""));
  const time = (path) => check(path, () => (!isBlank(getPath(c, path)) && !okTime(getPath(c, path)) ? "time" : ""));
  const url = (path) => check(path, () => (!okUrl(getPath(c, path)) ? "url" : ""));
  const price = (path) => check(path, () => (!okMoney(getPath(c, path)) ? "money" : ""));

  if (c.new_time_changes) {
    DAYS.forEach((day) => {
      days(`days.${day}.day_before_limit`);
      time(`days.${day}.time`);
      days(`days.${day}.day_after_limit`);
    });
  }
  check("timer", () => (!isBlank(c.timer) && !okInt(c.timer, 1, MAX_TIMER) ? "timer" : ""));

  if (c.lice_bez_ugovora) {
    const g = c.guest;
    if (g.restoran_delivery) {
      price("guest.delivery_price");
      price("guest.minimal_price");
    }
    if (g.post_delivery) {
      price("guest.post_price");
      price("guest.post_minimal_price");
    }
    check("guest.dani_bez_menija", () =>
      !isBlank(g.dani_bez_menija) && !okInt(g.dani_bez_menija, 0, MAX_ORDER_AHEAD) ? "ahead" : ""
    );
    check("guest.guest_cancel_order_time", () =>
      !isBlank(g.guest_cancel_order_time) && !okInt(g.guest_cancel_order_time, 0, MAX_CANCEL_MINUTES) ? "cancel" : ""
    );
    const from = toMin(c.delivery_from);
    const to = toMin(c.delivery_to);
    if (from !== null && to !== null && from >= to && (isDirty("delivery_from") || isDirty("delivery_to"))) {
      warnings.delivery_to = err("order");
    }
    if (c.guest_day.new_time_changes) {
      DAYS.forEach((day) => {
        const base = `guest_day.${day}`;
        time(`${base}.from`);
        time(`${base}.to`);
        days(`${base}.day_before_limit`);
        time(`${base}.time`);
        days(`${base}.day_after_limit`);
        const x = toMin(c.guest_day[day].from);
        const y = toMin(c.guest_day[day].to);
        if (x !== null && y !== null && x >= y && (isDirty(`${base}.from`) || isDirty(`${base}.to`))) {
          warnings[`${base}.to`] = err("order");
        }
      });
    }
  }
  [...LINK_FIELDS, ...MOB_LINK_FIELDS].forEach(url);
  if (!c.language.length && isDirty("language")) {
    warnings.language = err("language");
  }
  return { errors, warnings };
};

// ---------------------------------------------------------------- pretraga
/** Latinica bez dijakritika, mala slova: "Četvrtak" i "Четвртак" se traže sa "cetvrtak". */
export const normalizeText = (value) =>
  convert(String(value == null ? "" : value))
    .toLowerCase()
    .replace(/đ/g, "dj")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/**
 * @param {{ screen:string, id:string, title:string, own:string, all:string }[]} entries own = naziv i opis stavke, all = own + naziv ekrana (već normalizovano)
 * @param {string} query
 * @returns stavke koje sadrže sve riječi; prvo one kojima ih sadrži sam naziv/opis, zatim preko naziva ekrana
 */
export const rankSearch = (entries, query, limit = 8) => {
  const words = normalizeText(query).trim().split(/\s+/).filter(Boolean);
  if (!words.length) {
    return [];
  }
  const hits = [];
  entries.forEach((entry, index) => {
    if (!words.every((word) => entry.all.includes(word))) {
      return;
    }
    hits.push({ entry, index, rank: words.every((word) => entry.own.includes(word)) ? 0 : 1 });
  });
  hits.sort((x, y) => x.rank - y.rank || x.index - y.index);
  return hits.slice(0, limit).map((hit) => hit.entry);
};
